import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../i18n';

export default function VerificationQueueTab({
  api, formatDate, tableHeaderStyle, tableCellStyle, cardStyle, inputStyle, buttonPrimary, buttonSecondary }) {
  const { t } = useTranslation();
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/superadmin/verification/queue?status=${statusFilter}&limit=50`);
      setQueue(res.data?.requests || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/superadmin/verification/stats/overview');
      setStats(res.data);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { fetchQueue(); fetchStats(); }, [fetchQueue, fetchStats]);

  const handleApprove = async (requestId) => {
    try {
      await api.patch(`/superadmin/verification/${requestId}/approve?admin_notes=${encodeURIComponent(adminNotes)}`);
      setSelectedRequest(null);
      setAdminNotes('');
      fetchQueue();
      fetchStats();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to approve'); }
  };

  const handleReject = async (requestId) => {
    try {
      await api.patch(`/superadmin/verification/${requestId}/reject?admin_notes=${encodeURIComponent(adminNotes || 'Documents did not meet verification requirements.')}`);
      setSelectedRequest(null);
      setAdminNotes('');
      fetchQueue();
      fetchStats();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to reject'); }
  };

  const tierLabel = (tier) => tier === 2 ? 'Premium' : tier === 1 ? 'Verified' : 'Unverified';
  const statusBadge = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
      approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
    };
    return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[status] || colors.cancelled}`}>{status}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className={cardStyle + " p-4"}>
            <p className="text-2xl font-bold text-amber-600">{stats.queue?.pending || 0}</p>
            <p className="text-xs text-muted-foreground">Pending Review</p>
          </div>
          <div className={cardStyle + " p-4"}>
            <p className="text-2xl font-bold text-green-600">{stats.queue?.approved || 0}</p>
            <p className="text-xs text-muted-foreground">Total Approved</p>
          </div>
          <div className={cardStyle + " p-4"}>
            <p className="text-2xl font-bold text-red-600">{stats.queue?.rejected || 0}</p>
            <p className="text-xs text-muted-foreground">Rejected</p>
          </div>
          <div className={cardStyle + " p-4"}>
            <p className="text-2xl font-bold text-blue-600">{stats.tiers?.verified || 0}</p>
            <p className="text-xs text-muted-foreground">Verified Orgs</p>
          </div>
          <div className={cardStyle + " p-4"}>
            <p className="text-2xl font-bold text-amber-500">{stats.tiers?.premium || 0}</p>
            <p className="text-xs text-muted-foreground">Premium Orgs</p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 items-center">
        <span className="text-sm text-muted-foreground">Filter:</span>
        {['pending', 'approved', 'rejected', 'all'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded text-sm ${statusFilter === s ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Queue Table */}
      {loading ? (
        <p className="text-muted-foreground text-sm">Loading...</p>
      ) : queue.length === 0 ? (
        <p className="text-muted-foreground text-sm py-8 text-center">No {statusFilter} requests.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full">
            <thead>
              <tr>
                <th className={tableHeaderStyle}>Organization</th>
                <th className={tableHeaderStyle}>Submitted By</th>
                <th className={tableHeaderStyle}>Tier</th>
                <th className={tableHeaderStyle}>Documents</th>
                <th className={tableHeaderStyle}>Status</th>
                <th className={tableHeaderStyle}>Date</th>
                <th className={tableHeaderStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {queue.map(req => (
                <tr key={req._id} className="border-t border-border hover:bg-muted/50 cursor-pointer" onClick={() => setSelectedRequest(req)}>
                  <td className={tableCellStyle + " font-medium"}>{req.organization_name || '--'}</td>
                  <td className={tableCellStyle}>{req.user_name}<br/><span className="text-xs text-muted-foreground">{req.user_email}</span></td>
                  <td className={tableCellStyle}>{tierLabel(req.tier_requested)}</td>
                  <td className={tableCellStyle}>{req.documents?.length || 0}</td>
                  <td className={tableCellStyle}>{statusBadge(req.status)}</td>
                  <td className={tableCellStyle + " text-xs text-muted-foreground"}>{formatDate(req.created_at)}</td>
                  <td className={tableCellStyle}>
                    {req.status === 'pending' && (
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <button onClick={() => { setSelectedRequest(req); }} className="text-xs text-blue-600 hover:underline">Review</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedRequest(null)}>
          <div className="bg-background rounded-lg border border-border max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Verification Request</h3>
              <button onClick={() => setSelectedRequest(null)} className="text-muted-foreground hover:text-foreground">&times;</button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Organization:</span> <span className="font-medium">{selectedRequest.organization_name}</span></div>
                <div><span className="text-muted-foreground">Submitted by:</span> <span className="font-medium">{selectedRequest.user_name}</span></div>
                <div><span className="text-muted-foreground">Tier Requested:</span> {tierLabel(selectedRequest.tier_requested)}</div>
                <div><span className="text-muted-foreground">Current Tier:</span> {tierLabel(selectedRequest.current_tier)}</div>
                <div><span className="text-muted-foreground">Status:</span> {statusBadge(selectedRequest.status)}</div>
                <div><span className="text-muted-foreground">Submitted:</span> {formatDate(selectedRequest.created_at)}</div>
              </div>

              {selectedRequest.notes && (
                <div className="p-3 rounded bg-muted text-sm">
                  <p className="text-xs text-muted-foreground mb-1">Applicant Notes:</p>
                  {selectedRequest.notes}
                </div>
              )}

              {/* Documents */}
              <div>
                <h4 className="text-sm font-medium mb-2">Documents ({selectedRequest.documents?.length || 0})</h4>
                <div className="space-y-2">
                  {selectedRequest.documents?.map((doc, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded border border-border bg-card">
                      <div>
                        <p className="text-sm font-medium">{doc.file_name}</p>
                        <p className="text-xs text-muted-foreground">{doc.type_label || doc.type}</p>
                      </div>
                      <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline">
                        View Document
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              {/* Admin Notes + Actions */}
              {selectedRequest.status === 'pending' && (
                <div className="space-y-3 pt-2 border-t border-border">
                  <div>
                    <label className="text-sm font-medium">Admin Notes</label>
                    <textarea
                      value={adminNotes}
                      onChange={e => setAdminNotes(e.target.value)}
                      placeholder="Notes (visible to the applicant)..."
                      className={inputStyle + " w-full mt-1"}
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => handleApprove(selectedRequest._id)} className={buttonPrimary + " flex-1"}>
                      Approve (Tier {selectedRequest.tier_requested})
                    </button>
                    <button onClick={() => handleReject(selectedRequest._id)} className="flex-1 px-4 py-2 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700">
                      Reject
                    </button>
                  </div>
                </div>
              )}

              {/* Show admin notes if already reviewed */}
              {selectedRequest.admin_notes && selectedRequest.status !== 'pending' && (
                <div className="p-3 rounded bg-muted text-sm">
                  <p className="text-xs text-muted-foreground mb-1">Admin Notes:</p>
                  {selectedRequest.admin_notes}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
